import { API_ORIGIN, resolveApiPath, SITE } from '@/app/constants';
import type { ViduV1GetWechatSignatureReply } from '@/app/types';

interface WechatError {
  errMsg: string;
  [key: string]: unknown;
}

interface WechatShareConfig {
  title: string;
  desc: string;
  link: string;
  imgUrl: string;
  success?: () => void;
  cancel?: () => void;
  fail?: (res: WechatError) => void;
}

interface WechatJssdkConfig {
  debug?: boolean;
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
  openTagList: string[];
}

/**
 * 小程序 WebView 专有能力：只有页面被小程序的 web-view 承载时 jweixin 才会挂上它。
 * 普通 H5 里为 undefined，调用方必须判空（见 navigateToMiniappLiveDestination）。
 */
interface WxMiniProgramSDK {
  navigateBack?: (options?: { delta?: number; fail?: () => void }) => void;
  switchTab: (options: { url: string; fail?: () => void }) => void;
}

interface WxSDK {
  miniProgram?: WxMiniProgramSDK;
  config: (config: WechatJssdkConfig) => void;
  ready: (callback: () => void) => void;
  error: (callback: (res: WechatError) => void) => void;
  updateAppMessageShareData: (config: WechatShareConfig) => void;
  updateTimelineShareData: (config: Omit<WechatShareConfig, 'desc'>) => void;
  onMenuShareTimeline?: (config: Omit<WechatShareConfig, 'desc'>) => void;
  onMenuShareAppMessage?: (config: WechatShareConfig) => void;
}

declare global {
  interface Window {
    wx?: WxSDK;
  }
}

/**
 * 动态加载微信JSSDK脚本
 */
export const loadWechatScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // 如果已经加载过，直接返回
    if (typeof window !== 'undefined' && window.wx) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('微信JSSDK加载失败'));
    document.head.appendChild(script);
  });
};

/**
 * 获取当前页面URL
 */
export const getCurrentUrl = (): string => {
  if (typeof window === 'undefined') return '';

  let url = window.location.href;

  // 移除hash部分，因为微信签名不包含hash
  url = url.split('#')[0];

  return url;
};

/**
 * 获取微信JSSDK配置参数
 */
export const getWechatConfig = async (retryCount = 0): Promise<ViduV1GetWechatSignatureReply> => {
  try {
    const url = getCurrentUrl();
    const response = await fetch(
      `${API_ORIGIN}${resolveApiPath(`/vidu/v1/share/wechat/signature?url=${encodeURIComponent(url)}`)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
      },
    );

    if (!response.ok) {
      throw new Error(`获取微信配置失败: ${response.status}`);
    }

    const result = await response.json();

    // 验证返回的数据
    if (!result.signature || !result.timestamp || !result.noncestr) {
      throw new Error('微信配置数据不完整');
    }

    return result;
  } catch (error) {
    if (retryCount < 2) {
      console.warn('微信配置获取失败，尝试重试...', error);
      await new Promise((resolve) => setTimeout(resolve, 1000)); // 等待1秒后重试
      return getWechatConfig(retryCount + 1);
    }

    console.error('获取微信JSSDK配置失败:', error);
    throw error;
  }
};

/**
 * 初始化微信JSSDK
 */
export const initWechatJssdk = async (debug = false): Promise<void> => {
  if (typeof window === 'undefined' || SITE === 'global') return;

  try {
    // 1. 加载微信JSSDK
    await loadWechatScript();

    const wx = window.wx;
    if (!wx) {
      throw new Error('微信JSSDK未加载');
    }

    // 2. 获取JSSDK配置
    const config = await getWechatConfig();

    const currentUrl = getCurrentUrl();
    console.info('[WechatSDK] wx.config 调用', {
      appId: 'wxdcc2e452d266c2ff',
      url: currentUrl,
      timestamp: config.timestamp,
      nonceStr: config.noncestr,
      signaturePrefix: config.signature?.slice(0, 8),
      debug,
    });

    wx.config({
      debug,
      appId: 'wxdcc2e452d266c2ff',
      timestamp: config.timestamp ?? '',
      nonceStr: config.noncestr ?? '',
      signature: config.signature ?? '',
      jsApiList: [
        'updateAppMessageShareData',
        'updateTimelineShareData',
        'onMenuShareAppMessage', // 兼容旧版本
        'onMenuShareTimeline', // 兼容旧版本
        'hideMenuItems',
        'showMenuItems',
        'hideAllNonBaseMenuItem',
        'showAllNonBaseMenuItem',
        'closeWindow',
      ],
      openTagList: ['wx-open-launch-app'],
    });

    // 监听配置成功事件
    wx.ready(() => {
      console.info('[WechatSDK] wx.config 成功，JSSDK 就绪，openTagList 包含 wx-open-launch-app');
    });

    // 监听配置失败事件
    wx.error((err) => {
      console.error(
        '[WechatSDK] wx.config 失败 — 检查：1) abc.vidu.cn 是否在服务号 JS接口安全域名白名单；2) 签名 URL 是否与当前页面 URL 完全一致',
        err,
      );
    });
  } catch (error) {
    console.error('初始化微信JSSDK失败:', error);
    throw error;
  }
};

/**
 * 配置微信分享
 */
export const configWechatShare = (shareConfig: WechatShareConfig): void => {
  if (typeof window === 'undefined') return;

  const wx = window.wx;
  if (!wx) {
    console.error('微信JSSDK未加载，无法配置分享');
    return;
  }

  // 确保分享链接是完整的URL
  let shareLink = shareConfig.link;
  if (shareLink && !shareLink.startsWith('http')) {
    shareLink = window.location.origin + shareLink;
  }

  // 确保图片URL是完整的URL
  let imgUrl = shareConfig.imgUrl;
  if (imgUrl && !imgUrl.startsWith('http')) {
    imgUrl = window.location.origin + imgUrl;
  }

  const finalConfig = {
    ...shareConfig,
    link: shareLink,
    imgUrl: imgUrl,
  };

  // 使用新版分享接口
  wx.ready(() => {
    try {
      // 分享给朋友
      wx.updateAppMessageShareData({
        title: finalConfig.title,
        desc: finalConfig.desc,
        link: finalConfig.link,
        imgUrl: finalConfig.imgUrl,
        success: () => {
          console.info('分享给朋友配置成功');
          finalConfig.success?.();
        },
        cancel: finalConfig.cancel,
        fail: (error) => {
          console.error('分享给朋友配置失败:', error);
          finalConfig.fail?.(error);
        },
      });

      // 分享到朋友圈
      wx.updateTimelineShareData({
        title: finalConfig.title,
        link: finalConfig.link,
        imgUrl: finalConfig.imgUrl,
        success: () => {
          console.info('分享到朋友圈配置成功');
          finalConfig.success?.();
        },
        cancel: finalConfig.cancel,
        fail: (error) => {
          console.error('分享到朋友圈配置失败:', error);
          finalConfig.fail?.(error);
        },
      });

      // 兼容旧版本分享到好友
      wx.onMenuShareAppMessage?.({
        title: finalConfig.title,
        desc: finalConfig.desc,
        link: finalConfig.link,
        imgUrl: finalConfig.imgUrl,
        success: finalConfig.success,
        cancel: finalConfig.cancel,
        fail: finalConfig.fail,
      });

      // 兼容旧版本分享到朋友圈
      wx.onMenuShareTimeline?.({
        title: finalConfig.title,
        link: finalConfig.link,
        imgUrl: finalConfig.imgUrl,
        success: finalConfig.success,
        cancel: finalConfig.cancel,
        fail: finalConfig.fail,
      });
    } catch (error) {
      console.error('配置微信分享时发生错误:', error);
      finalConfig.fail?.(error as WechatError);
    }
  });
};
